import React, { useEffect, useState, useRef } from "react";
import "./sub.css";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import { API_URL } from "../confi";

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

const LatestThreeBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  const BLOGS_PER_PAGE_MOBILE = 1;
  const BLOGS_PER_PAGE_DESKTOP = 3;

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/blogs`, {
          headers: {
            Accept: "application/json",
          },
        });

        const data = await res.json();
        // console.log("Fetch Blogs Response:", JSON.stringify(data, null, 2));

        if (res.ok) {
          const allBlogs = data.blogs?.data || data.blogs || [];
          setBlogs(allBlogs);
          setError(null);
        } else {
          setError(data.message || "Failed to fetch blogs");
        }
      } catch (err) {
        // console.error("Fetch blogs error:", err);
        setError("Failed to fetch blogs");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Calculate total pages
  const totalPages = isMobile 
    ? Math.ceil(blogs.length / BLOGS_PER_PAGE_MOBILE)
    : Math.ceil(blogs.length / BLOGS_PER_PAGE_DESKTOP);

  // Get current page blogs
  const getCurrentPageBlogs = () => {
    if (isMobile) {
      const startIndex = currentPage * BLOGS_PER_PAGE_MOBILE;
      return [...blogs]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(startIndex, startIndex + BLOGS_PER_PAGE_MOBILE);
    } else {
      const startIndex = currentPage * BLOGS_PER_PAGE_DESKTOP;
      return [...blogs]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(startIndex, startIndex + BLOGS_PER_PAGE_DESKTOP);
    }
  };

  // Handle scroll for mobile
  const handleScroll = () => {
    if (!scrollContainerRef.current || !isMobile) return;
    
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const cardWidth = scrollContainerRef.current.offsetWidth;
    const newPage = Math.round(scrollLeft / cardWidth);
    setCurrentPage(newPage);
  };

  // Go to specific page
  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
    
    if (isMobile && scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: pageIndex * containerWidth,
        behavior: 'smooth'
      });
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1);
    }
  };

  // Previous page
  const prevPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  };

  const renderBlogCard = (blog, index) => {
  const slug = blog.title
    ?.toLowerCase()
    ?.trim()
    ?.replace(/[^a-z0-9\s-]/g, "")
    ?.replace(/\s+/g, "-");

  const imageUrl = blog.image?.startsWith("http")
    ? blog.image
    : `https://jph.purvsoft.com/${blog.image}`;

  const blogData = {
    id: blog.id,
    title: blog.title,
    content: blog.content || "",
    author: blog.author || "Admin",
    date: blog.created_at || new Date().toISOString(),
    category: blog.category || "General",
    image: imageUrl,
    excerpt:
      blog.excerpt ||
      (blog.content ? blog.content.substring(0, 150) + "..." : ""),
  };

  return (
    <Link
      key={`blog-${index}`}
      to={`/blog/${slug}`}
      state={{ blog: blogData }}
      className="blog-card1-link"
    >
      <div className="blog-card1">
        <p className="blog-date1">{formatDate(blog.created_at)}</p>
        <h3 className="blog-title1">{blog.title}</h3>
        <img src={imageUrl} alt={blog.title} className="blog-image1" />
        <div className="blog-footer1">
          <button className="blog-arrow1" type="button">
            <FaArrowRight />
          </button>
        </div>
      </div>
    </Link>
  );
};


  return (
    <>
      <div className="blog-standard-container1">
        <h2
          className="service-h2"
          style={{ textDecoration: "underline", textAlign: "center", color: "#7a2f7b" }}
        >
          Latest Blogs
        </h2>

        {loading ? (
          <div className="loader1"></div>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : blogs.length === 0 ? (
          <p>No blogs available.</p>
        ) : (
          <>
            {/* Desktop View - Grid Layout with Pagination */}
            {!isMobile && (
              <>
                <div className="blog-grid1">
                  {getCurrentPageBlogs().map((blog, index) => 
                    renderBlogCard(blog, index)
                  )}
                </div>
                
                {/* Desktop Pagination */}
                {totalPages > 1 && (
                  <div className="blog-pagination-controls">
                    <button 
                      className="pagination-btn1" 
                      onClick={prevPage}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </button>
                    
                    <div className="page-numbers">
                      {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                          key={index}
                          className={`page-number ${currentPage === index ? 'active' : ''}`}
                          onClick={() => goToPage(index)}
                        >
                          {index + 1}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn1" 
                      onClick={nextPage}
                      disabled={currentPage === totalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Mobile View - Horizontal Slider */}
            {isMobile && (
              <>
                <div className="mobile-blog-slider-container">
                  <button 
                    className="arrow-btn left-arrow" 
                    onClick={prevPage}
                    disabled={currentPage === 0}
                  >
                    <FaArrowLeft />
                  </button>
                  
                  <div 
                    className="blog-scroll-container"
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                  >
                    <div className="blog-scroll-content">
                      {[...blogs]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((blog, index) => (
                          <div className="blog-card1 mobile-blog-card" key={index}>
                            {renderBlogCard(blog, index)}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  
                  <button 
                    className="arrow-btn right-arrow1" 
                    onClick={nextPage}
                    disabled={currentPage === totalPages - 1}
                  >
                   <FaArrowRight />
                  </button>
                </div>

                {/* Mobile Dot Pagination */}
                {totalPages > 1 && (
                  <div className="dot-pagination">
                    {Array.from({ length: totalPages }).map((_, index) => (
                      <button
                        key={index}
                        className={`dot ${currentPage === index ? 'active' : ''}`}
                        onClick={() => goToPage(index)}
                        aria-label={`Go to page ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default LatestThreeBlog;